import React from 'react';
import { useLocation, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function UserDetail() {
    const { t } = useTranslation();
    const location = useLocation();
    const { user } = location.state || {}; // Safely destructure user from state
    const { id } = useParams();

    if (!user) {
        return (
            <div className="p-6 bg-gray-100 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <p className="text-xl text-gray-700">{t('user_not_found_or_direct_access') || 'User data not found or page accessed directly.'}</p>
                    <Link to="/admin/userlist" className="mt-4 inline-block bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                        {t('back_to_user_list') || 'Back to User List'}
                    </Link>
                </div>
            </div>
        );
    }

    const DetailItem = ({ label, value }) => (
        <tr className="border-b">
            <td className="p-4 text-gray-600 font-medium">{label}</td>
            <td className="p-4 font-semibold text-gray-900">{value || '-'}</td>
        </tr>
    );

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto">
                <div className="mb-6">
                    <Link to="/admin/userlist" className="text-blue-600 hover:text-blue-800 flex items-center">
                        <span className="mr-2">←</span> {t('back_to_user_list') || 'Back to User List'}
                    </Link>
                </div>

                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="p-6 border-b flex items-center space-x-4">
                        <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-600">
                            {user.firstName ? user.firstName[0].toUpperCase() : '?'}
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">
                                {user.firstName || ''} {user.lastName || ''}
                            </h1>
                            <p className="text-md text-gray-500">{user.email}</p>
                        </div>
                    </div>

                    <div className="p-6">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4">{t('user_details') || 'User Details'}</h2>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <tbody>
                                    <DetailItem label={t('user_id')} value={user.studentId} />
                                    <DetailItem label={t('username')} value={user.username} />
                                    <DetailItem label={t('first_name')} value={user.firstName} />
                                    <DetailItem label={t('last_name')} value={user.lastName} />
                                    <DetailItem label={t('email')} value={user.email} />
                                    <DetailItem label={t('status')} value={<span className={`px-2 py-1 text-sm rounded-full ${user.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{user.status}</span>} />
                                    <DetailItem label={t('preferred_language')} value={user.preferredLanguage} />
                                    <DetailItem label={t('country_code')} value={user.countryCode} />
                                    <DetailItem label={t('gender')} value={user.gender} />
                                    <DetailItem label={t('birth_date')} value={user.birthDate ? new Date(user.birthDate).toLocaleDateString() : '-'} />
                                    <DetailItem label={t('nickname')} value={user.nickname} />
                                    <DetailItem label={t('creation_date')} value={user.creationDate ? new Date(user.creationDate).toLocaleDateString() : '-'} />
                                    <DetailItem
                                        label={t('roles')}
                                        value={
                                            <div className="flex flex-wrap gap-2">
                                                {user.userRoles?.map((role, index) => (
                                                    <span key={index} className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        {role.replace('zubrus_', '')}
                                                    </span>
                                                ))}
                                            </div>
                                        }
                                    />
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
